import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, Loader2 } from 'lucide-react';

type Role = 'broker' | 'carrier' | 'shipper' | 'other';

const ROLE_LABELS: Record<Role, string> = {
  broker: 'Broker',
  carrier: 'Carrier',
  shipper: 'Shipper',
  other: 'Other',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

const digits = (s: string) => s.replace(/\D/g, '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

const PartnerApplicationDialog: React.FC<Props> = ({ open, onOpenChange, source = 'partner_program_page' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const valid =
    name.trim().length > 1 &&
    EMAIL_RE.test(email.trim()) &&
    digits(phone).length >= 10 &&
    digits(phone).length <= 15 &&
    !!role;

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setRole(''); setCompany('');
    setMessage(''); setWebsite(''); setError(null); setDone(false); setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('submit-partner-application', {
        body: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          role,
          company: company.trim(),
          message: message.trim(),
          website: website.trim(),
          source,
        },
      });
      const payload = data as { ok?: boolean; error?: string } | null;
      if (fnErr || !payload?.ok) {
        setError(payload?.error || 'Something went wrong. Please try again or email support@lanerunner.com.');
        return;
      }
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again or email support@lanerunner.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-[520px] overflow-y-auto">
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12" style={{ color: '#02857E' }} />
            <DialogTitle className="mt-4 text-lg">Application received</DialogTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              Thanks — a LaneRunner representative will contact you shortly.
            </p>
            <Button className="mt-6" onClick={() => handleOpenChange(false)}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Apply to become a partner</DialogTitle>
              <DialogDescription>
                Tell us a bit about you and we'll follow up with your partner details.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pa-name">Name *</Label>
                  <Input id="pa-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pa-email">Email *</Label>
                  <Input id="pa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pa-phone">Phone *</Label>
                  <Input id="pa-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pa-role">Which are you? *</Label>
                  <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                    <SelectTrigger id="pa-role"><SelectValue placeholder="Select one" /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
                        <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pa-company">Company <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="pa-company" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pa-message">How will you refer? <span className="text-muted-foreground">(optional)</span></Label>
                <Textarea id="pa-message" rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
              </div>

              {/* honeypot — hidden from humans */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="pa-website">Website</label>
                <input id="pa-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
              )}

              <Button type="submit" className="w-full" disabled={!valid || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitting ? 'Submitting…' : 'Submit application'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PartnerApplicationDialog;
